import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { JiraIssueLiteData } from '../../../../api/endpoints/jira';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { Input, Menu, Spinner } from '../../../../ui';
import { Position } from '../../../../ui/Position';
import { FixedScroller } from '../../../../ui/Scrollers';
import { useResolve } from '../../../Container';
import { UIField } from '../../../canvas/entities/UIField';
import { PopperFieldEditor, PopperFieldEditorContentProps } from '../../../canvas/workspace/adapters/PopperFieldEditor';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { jiraCardSetParent } from '../AtlassianCommands';
import { JiraCard } from '../JiraCard';
import { JiraOAuthRequiredPopperContent } from './JiraOAuthRequiredPopperContent';
import { JiraPopperActionMenu } from './JiraPopperActionMenu';

const useJiraCardParentQuery = (cards: JiraCard[], query: string) => {
    const api = useResolve(ApiClient);

    const mainCard = cards[0];
    const jql = `
        project = ${mainCard.project} 
        AND type = Epic 
        ${query ? `AND text ~ "${query}"` : ''}
        ORDER BY created DESC
    `;

    return useQuery({
        queryKey: ['jira.issues.list', 'parentsOf', mainCard.id, query],
        queryFn: async () => api.exec(v2.jira.issues.list({ jql })),
    });
};

export const JiraParentMenu = ({ cards, onClose }: { cards: JiraCard[], onClose(): void }) => {

    const dispatch = useResolve(CommandDispatcher);
    const [query, setQuery] = useState('');
    const { isLoading, data } = useJiraCardParentQuery(cards, query);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    useEffect(() => {
        if (!isLoading) setHasLoadedOnce(true);
    }, [isLoading]);

    const onClick = (tm: JiraIssueLiteData | null) => {
        dispatch(jiraCardSetParent, [tm, cards.map(x => x.id)]);
        onClose();
    };

    return <JiraPopperActionMenu loading={!hasLoadedOnce} label="Set Epic">
        <Flexer relative minWidth="24" margin="0 .5rem .5rem .5rem">
            <Input type="text" placeholder="Search" value={query} onChange={setQuery} autoFocus compact />
            {isLoading && <Position at="right">
                <Spinner size={1.25} stroke={3} style={{ marginRight: '.5rem' }} />
            </Position>}
        </Flexer>
        <FixedScroller maxHeight="12">
            {data?.items?.map((tm, i) =>
                <Menu.Item role="menuitem" aria-label={tm.summary} key={i} onClick={() => onClick(tm)}>
                    <img style={{ width: '1.5rem', height: '1.5rem' }} src={tm.issueType.iconUrl} />
                    <Menu.ItemTextCropped maxWidth="16">
                        {tm.summary}
                    </Menu.ItemTextCropped>
                </Menu.Item>
            )}
            {!!data?.items?.length && <Menu.Divide />}
            <Menu.Item role="menuitem" aria-label="Clear Epic" onClick={() => onClick(null)}>
                <Menu.ItemTextCropped style={{ marginLeft: '1.5rem' }}>
                    Set Empty
                </Menu.ItemTextCropped>
            </Menu.Item>
        </FixedScroller>
    </JiraPopperActionMenu>;
};

const JiraParentMenuFieldEditor = ({ field, onClose }: PopperFieldEditorContentProps) =>
    <JiraParentMenu cards={[field.owner.cast(JiraCard)]} onClose={onClose} />;

export const JiraParentField = (owner: JiraCard): UIField => ({
    ui: PopperFieldEditor(JiraOAuthRequiredPopperContent(JiraParentMenuFieldEditor)),
    owner,
    name: 'parent',
    bounds: owner.layout.parent,
});
